import { ART_PALETTE } from '../art-palette.js';
import { floraPixels, plainTile } from './helpers.js';

// ---------------------------------------------------------------------------
// House style (docs/art-direction-rules.md §0, flat hybrid — LOCKED)
//
// Trees are drawn as flat masses: a full Ink contour, exactly three canopy
// values off the grass ramp, three wood values on the trunk, and a flat Dark
// grass contact ellipse behind the foot. Species read from SILHOUETTE and from
// where their triple sits on the ramp — never from texture density. The only
// sanctioned canopy texture is the leaf-clump notch motif biting into the
// lit/body boundary.
// ---------------------------------------------------------------------------

const INK = 1;
/** Contact shadow (§0.5): flat Dark grass, always drawn behind the tree. */
const CONTACT_SHADOW = 13;
/** Shared wood ramp for every trunk: dark underside, body, lit left plane. */
const BARK = { dark: 3, body: 7, lit: 8 };

/**
 * Canopy triples, dark → body → lit, each species sitting one step further up
 * the grass ramp. Silhouette does the species work (§3); these supply the hue
 * bias — pine deepest, oak mid green, maple bright, aspen a pale chartreuse
 * cap. Every triple keeps a visible step between all three values.
 */
const CANOPY_VALUES = {
  pine: { dark: 12, body: 13, lit: 14 },
  oak: { dark: 12, body: 14, lit: 15 },
  maple: { dark: 13, body: 15, lit: 16 },
  aspen: { dark: 13, body: 15, lit: 17 },
};

/** Integer hash in [0,1). Avoids the diagonal banding of (x * a + y * b) % m. */
function hash(x, y) {
  let v = Math.imul(x, 0x1f123bb5) ^ Math.imul(y, 0x5f356495);
  v ^= v >>> 15; v = Math.imul(v, 0x2c1b3c6d); v ^= v >>> 12;
  return (v >>> 0) / 0x100000000;
}

const NEIGHBORS_8 = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];

/** Wrap every opaque cluster in Ink, diagonal corner gaps included (§0.1). */
function inkContour(pixels, ink = INK) {
  const copy = pixels.map((row) => [...row]);
  for (let y = 0; y < pixels.length; y++) {
    for (let x = 0; x < pixels[y].length; x++) {
      if (copy[y][x]) continue;
      if (NEIGHBORS_8.some(([dx, dy]) => copy[y + dy]?.[x + dx])) pixels[y][x] = ink;
    }
  }
}

/**
 * Three flat values from a solid mask: darkest where the mass turns away
 * (bottom / right rim), lightest on the upper-left cap, body everywhere else.
 * `dx`/`dy` set rim depth, `capX`/`capY` how far the lit cap reaches in.
 */
function shadeMass(solid, w, h, { dx, dy, capX, capY }) {
  const value = Array.from({ length: h }, () => Array(w).fill(0));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!solid(x, y)) continue;
    if (!solid(x, y + dy) || !solid(x + dx, y)) value[y][x] = 1;
    else value[y][x] = solid(x + capX, y + capY) ? 3 : 2;
  }
  return value;
}

/**
 * Leaf-clump motif (§0, foliage canopy): a small family of 3 notch shapes
 * stamped in body colour along the lit/body boundary, spaced on a jittered
 * walk of that boundary. Never a per-pixel formula.
 */
const NOTCH_SHAPES = [
  [[0, 0], [1, 0], [0, -1]],
  [[0, 0], [-1, 0], [0, -1], [-1, -1]],
  [[0, 0], [0, -1], [0, -2]],
];
function notchLitBoundary(value, w, h, spacing) {
  const boundary = [];
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    if (value[y][x] !== 3) continue;
    if (value[y + 1][x] === 2 || value[y][x + 1] === 2) boundary.push([x, y]);
  }
  if (boundary.length < spacing) return;
  for (let i = Math.floor(spacing / 2); i < boundary.length; i += spacing) {
    const [bx, by] = boundary[i];
    if (hash(bx, by) < 0.3) continue;
    for (const [ox, oy] of NOTCH_SHAPES[Math.floor(hash(by, bx) * 3) % 3]) {
      if (value[by + oy]?.[bx + ox] === 3) value[by + oy][bx + ox] = 2;
    }
  }
}

// ---------------------------------------------------------------------------
// Static tree sprites — the art that actually renders in the world.
// ---------------------------------------------------------------------------

/**
 * Canopy lobes per species / growth stage as [cx, cy, rx, ry].
 *
 * The three broadleaf species are separated by proportion alone:
 *   aspen — narrow vertical column, tallest and thinnest crown
 *   maple — medium width, peaked top, arms held high
 *   oak   — much wider than tall, round top, heavy low skirt
 */
const CANOPY_LOBES = {
  aspen: [
    [[16, 15, 5, 12], [13, 22, 4, 5], [19, 20, 4, 5]],
    [[24, 21, 8, 17], [19, 31, 5, 8], [29, 29, 5, 8], [24, 37, 6, 7]],
    [[32, 27, 10, 22], [25, 39, 7, 10], [39, 37, 7, 10], [32, 47, 8, 9]],
  ],
  maple: [
    [[16, 23, 6, 7], [16, 17, 6, 7], [11, 25, 4, 4], [21, 25, 4, 4]],
    [[24, 31, 10, 11], [24, 22, 9, 10], [15, 36, 7, 6], [33, 36, 7, 6], [24, 39, 8, 5]],
    [[32, 36, 14, 13], [32, 26, 12, 14], [19, 41, 9, 8], [45, 41, 9, 8], [32, 46, 11, 6]],
  ],
  oak: [
    [[16, 23, 9, 7], [16, 18, 6, 4], [8, 26, 4, 4], [24, 26, 4, 4]],
    [[24, 31, 14, 10], [24, 24, 10, 6], [11, 36, 7, 6], [37, 36, 7, 6], [24, 38, 12, 6]],
    [[32, 36, 20, 13], [32, 24, 15, 10], [13, 42, 10, 8], [51, 42, 10, 8], [32, 46, 16, 8]],
  ],
};

/** Conifer tiers as [top, bottom, half-width]; overlap gives the stepped skirt. */
const CONIFER_TIERS = [
  [[3, 18, 7], [14, 31, 11]],
  [[2, 17, 8], [13, 30, 13], [26, 44, 18]],
  [[2, 20, 10], [15, 35, 16], [30, 48, 22], [43, 58, 27]],
];

/** Trunk half-width: a sapling whip, a young pole, an old bole. */
const TRUNK_HALF = { aspen: [1, 1, 2], maple: [1, 2, 3], oak: [1, 3, 4], pine: [1, 2, 3] };

function staticTreeSprite(species, stage) {
  const sizes = [[32, 48], [48, 64], [64, 80]];
  const [width, height] = sizes[stage];
  const cx = Math.floor(width / 2);
  const leaf = CANOPY_VALUES[species];
  const art = Array.from({ length: height }, () => Array(width).fill(0));
  const canopy = Array.from({ length: height }, () => Array(width).fill(false));
  const put = (x, y, color) => { if (art[y]?.[x] !== undefined) art[y][x] = color; };

  // --- Silhouette first (§3): build the canopy mask, nothing else. ---
  if (species === 'pine') {
    for (const [top, bottom, halfWidth] of CONIFER_TIERS[stage]) {
      for (let y = top; y <= bottom; y++) {
        const t = (y - top + 1) / (bottom - top + 1);
        let half = Math.max(1, Math.round(halfWidth * t));
        if (y === bottom) half = Math.max(1, half - 1);
        for (let x = cx - half; x <= cx + half; x++) if (canopy[y]?.[x] !== undefined) canopy[y][x] = true;
      }
    }
  } else {
    for (const [lx, ly, rx, ry] of CANOPY_LOBES[species][stage]) {
      for (let y = Math.ceil(ly - ry); y <= ly + ry; y++) {
        for (let x = Math.ceil(lx - rx); x <= lx + rx; x++) {
          if (((x - lx) / rx) ** 2 + ((y - ly) / ry) ** 2 <= 1 && canopy[y]?.[x] !== undefined) {
            canopy[y][x] = true;
          }
        }
      }
    }
  }

  // --- Trunk: three wood values and a root flare, never a uniform stick. ---
  const trunkHalf = TRUNK_HALF[species][stage];
  const baseY = height - 6;
  let trunkTop = 0;
  for (let y = 0; y < height && !trunkTop; y++) {
    let covered = true;
    for (let x = cx - trunkHalf - 1; x <= cx + trunkHalf + 1; x++) if (!canopy[y][x]) { covered = false; break; }
    if (covered) trunkTop = y;
  }

  const flareRows = [4, 5, 6][stage];
  const flareGain = [2, 3, 4][stage];
  const foot = trunkHalf + flareGain;
  const trunkRow = (y, half, root) => {
    const darkCols = Math.max(1, Math.round(half * 0.6));
    const litCols = half >= 3 ? 2 : 1;
    for (let x = cx - half; x <= cx + half; x++) {
      let color = BARK.body;
      if (x < cx - half + litCols) color = BARK.lit;
      else if (x >= cx + half - darkCols + 1) color = BARK.dark;
      if (root) color = x < cx - half + litCols ? BARK.body : BARK.dark;
      put(x, y, color);
    }
  };
  for (let y = trunkTop; y <= baseY; y++) {
    const into = y - (baseY - flareRows);
    const half = into > 0 ? trunkHalf + Math.round(flareGain * (into / flareRows) ** 2) : trunkHalf;
    trunkRow(y, half, into > flareRows - 2);
  }
  // Root tongues: the left root runs longer and lower than the right one, so the
  // foot is an asymmetric flare rather than a symmetric wedge.
  const rootSpan = (direction, reach, rows) => {
    for (let row = 0; row < rows; row++) {
      const y = baseY - row;
      const length = Math.round(reach * (1 - row / rows));
      for (let i = 1; i <= length; i++) put(cx + direction * (foot + i), y, row ? BARK.body : BARK.dark);
    }
  };
  rootSpan(-1, flareGain + 2, 2);
  rootSpan(1, flareGain + 1, 2);

  // --- Canopy over the trunk, then three flat values across the whole mass. ---
  const solid = (x, y) => canopy[y]?.[x] === true;
  let minX = width; let maxX = 0; let minY = height; let maxY = 0;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (!canopy[y][x]) continue;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const spanX = maxX - minX + 1; const spanY = maxY - minY + 1;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const value = shadeMass(solid, width, height, {
    dx: clamp(Math.round(spanX * 0.09), 2, 3),
    dy: clamp(Math.round(spanY * 0.09), 2, 4),
    capX: clamp(Math.round(spanX * 0.22), 3, 13),
    capY: clamp(Math.round(spanY * 0.22), 3, 13),
  });
  notchLitBoundary(value, width, height, [4, 6, 8][stage]);
  const step = [leaf.dark, leaf.body, leaf.lit];
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (value[y][x]) put(x, y, step[value[y][x] - 1]);
  }

  // Overlap occlusion (§4): a single Ink line where the canopy sits on the trunk.
  for (let x = cx - trunkHalf - 1; x <= cx + trunkHalf + 1; x++) {
    for (let y = height - 1; y >= 0; y--) {
      if (!canopy[y]?.[x]) continue;
      if (art[y + 1]?.[x] && !canopy[y + 1][x]) put(x, y + 1, INK);
      break;
    }
  }

  inkContour(art);

  // --- Contact shadow (§0.5), flat Dark grass, composited behind the tree. ---
  const pixels = Array.from({ length: height }, () => Array(width).fill(0));
  const shadowRx = foot + flareGain + 4 + (species === 'oak' ? 3 : 0);
  const shadowCy = baseY + 2; const shadowRy = 2 + stage * 0.5;
  for (let y = Math.ceil(shadowCy - shadowRy); y <= shadowCy + shadowRy; y++) {
    for (let x = cx - shadowRx; x <= cx + shadowRx; x++) {
      if (((x - cx) / shadowRx) ** 2 + ((y - shadowCy) / shadowRy) ** 2 > 1) continue;
      if (pixels[y]?.[x] !== undefined) pixels[y][x] = CONTACT_SHADOW;
    }
  }
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (art[y][x]) pixels[y][x] = art[y][x];
  }

  return { width, height, pixels, palette: ART_PALETTE };
}

/** Crown radii for the 16x16 picker icon, as [rx, ry, cy] per growth stage. */
const PREVIEW_CROWN = {
  aspen: [[2, 4, 6], [2.6, 5, 6], [3.2, 5.6, 6]],
  maple: [[3, 3.4, 6], [4, 4.2, 6], [5, 5, 6]],
  oak: [[3.8, 2.8, 7], [5, 3.4, 7], [6, 4, 7]],
};
const PREVIEW_TIERS = [[[3, 11, 3]], [[2, 8, 3], [6, 12, 5]], [[1, 6, 3], [5, 12, 6]]];

/** 16x16 picker icon: the same grammar shrunk to one cell. */
function staticTreePreview(species, stage) {
  const leaf = CANOPY_VALUES[species];
  return floraPixels(({ pixels, put }) => {
    const cx = 7;
    const baseY = 13;
    const canopy = Array.from({ length: 16 }, () => Array(16).fill(false));
    if (species === 'pine') {
      for (const [top, bottom, halfWidth] of PREVIEW_TIERS[stage]) {
        for (let y = top; y <= bottom; y++) {
          const grown = Math.round(halfWidth * (y - top + 1) / (bottom - top + 1));
          const half = Math.max(0, grown - (y === bottom ? 1 : 0));
          for (let x = cx - half; x <= cx + half; x++) if (canopy[y]?.[x] !== undefined) canopy[y][x] = true;
        }
      }
    } else {
      const [rx, ry, ccy] = PREVIEW_CROWN[species][stage];
      for (let y = 1; y < 15; y++) for (let x = 1; x < 15; x++) {
        if (((x - cx) / rx) ** 2 + ((y - ccy) / ry) ** 2 <= 1) canopy[y][x] = true;
      }
    }

    // Trunk: lit left column, body, dark right column, splayed at the foot.
    const trunkHalf = stage === 2 ? 1 : 0;
    let canopyBottom = 0;
    for (let y = 0; y < 16; y++) if (canopy[y][cx]) canopyBottom = y;
    for (let y = canopyBottom + 1; y <= baseY; y++) {
      for (let x = cx - trunkHalf; x <= cx + trunkHalf + 1; x++) {
        put(x, y, x === cx - trunkHalf ? BARK.lit : x > cx ? BARK.dark : BARK.body);
      }
    }
    put(cx - trunkHalf - 1, baseY, BARK.dark);
    put(cx + trunkHalf + 2, baseY, BARK.dark);
    if (stage === 2) { put(cx - trunkHalf - 2, baseY, BARK.dark); put(cx + trunkHalf + 3, baseY, BARK.dark); }

    const solid = (x, y) => canopy[y]?.[x] === true;
    const value = shadeMass(solid, 16, 16, { dx: 2, dy: 2, capX: 3, capY: 4 });
    notchLitBoundary(value, 16, 16, 4);
    const step = [leaf.dark, leaf.body, leaf.lit];
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      if (value[y][x]) put(x, y, step[value[y][x] - 1]);
    }
    inkContour(pixels);
    for (let x = cx - 4; x <= cx + 5; x++) if (!pixels[15][x]) pixels[15][x] = CONTACT_SHADOW;
  });
}

const STATIC_TREES = ['aspen', 'maple', 'oak', 'pine'].flatMap((species) =>
  ['sapling', 'young', 'old'].map((age, stage) => plainTile({
    key: `tree.${species}-${age}`, name: `${age[0].toUpperCase() + age.slice(1)} ${species}`,
    channel: 'treeWood', section: 'treeWood', component: 'wall', pixels: staticTreePreview(species, stage),
    collision: { ground: true }, treePart: 'static', staticTreeSprite: staticTreeSprite(species, stage),
  })));

export { STATIC_TREES };
